import React from 'react';
import { Box, Container, Heading, Text, VStack, Link, useColorMode } from '@chakra-ui/react';
import { useAppData } from '../store/dataStore';

const PrivacyPolicy = () => {
  const { colorMode } = useColorMode();
  const { appData } = useAppData();

  const bgColor = colorMode === 'dark' ? 'gray.900' : 'gray.50';
  const textColor = colorMode === 'dark' ? 'gray.100' : 'gray.800';
  const sectionBg = colorMode === 'dark' ? 'gray.800' : 'white';

  return (
    <Box bg={bgColor} minH="100vh" py={12}>
      <Container maxW="2xl">
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading as="h1" size="2xl" mb={2} color="blue.600">
              Privacy Policy
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Last updated: {new Date().toLocaleDateString()}
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              1. Overview
            </Heading>
            <Text color={textColor} lineHeight="1.8">
              Ojas Amirai ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services (the "Service").
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              2. Information We Collect
            </Heading>
            <VStack align="start" spacing={3}>
              <Box>
                <Heading as="h3" size="md" mb={2} color="blue.600">
                  Personal Information
                </Heading>
                <Text color={textColor} lineHeight="1.8">
                  When you register, enroll, or contact us, we collect information such as:
                </Text>
                <Text as="ul" color={textColor} ml={4} mt={2} lineHeight="1.8">
                  <li>• Name and email address</li>
                  <li>• Phone number</li>
                  <li>• Educational background</li>
                  <li>• Course enrollment information</li>
                </Text>
              </Box>
              <Box>
                <Heading as="h3" size="md" mb={2} color="blue.600">
                  Usage Data
                </Heading>
                <Text color={textColor} lineHeight="1.8">
                  We automatically collect information about your interaction with our Service, including IP address, browser type, pages visited, and timestamps.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              3. How We Use Your Information
            </Heading>
            <Text as="ul" color={textColor} ml={4} lineHeight="1.8">
              <li>• To provide, maintain, and improve our Service</li>
              <li>• To send course updates, webinar invitations, and promotional materials</li>
              <li>• To process course enrollments and payments</li>
              <li>• To respond to inquiries and provide customer support</li>
              <li>• To analyze usage patterns and improve user experience</li>
              <li>• To comply with legal obligations</li>
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              4. Third-Party Services
            </Heading>
            <Text color={textColor} lineHeight="1.8">
              We may use third-party services for authentication (OAuth), analytics, payment processing, and communication. These services have their own privacy policies governing how they handle your data.
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              5. Data Security
            </Heading>
            <Text color={textColor} lineHeight="1.8">
              We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is completely secure.
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              6. Your Rights
            </Heading>
            <Text color={textColor} lineHeight="1.8" mb={3}>
              You have the right to:
            </Text>
            <Text as="ul" color={textColor} ml={4} lineHeight="1.8">
              <li>• Access your personal data</li>
              <li>• Request correction of inaccurate data</li>
              <li>• Request deletion of your data</li>
              <li>• Opt-out of marketing communications</li>
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              7. Contact Us
            </Heading>
            <Text color={textColor} lineHeight="1.8">
              If you have questions about this Privacy Policy or your personal data, please contact us at:
            </Text>
            <Text color={textColor} mt={3} lineHeight="1.8">
              <strong>Email:</strong>{' '}
              <Link href={`mailto:${appData.supportEmail}`} color="blue.600" isExternal>
                {appData.supportEmail}
              </Link>
            </Text>
            <Text color={textColor} lineHeight="1.8">
              <strong>Phone:</strong>{' '}
              <Link href={`tel:${appData.phone}`} color="blue.600">
                {appData.phone}
              </Link>
            </Text>
          </Box>

          <Box bg={sectionBg} p={6} borderRadius="md" boxShadow="sm">
            <Heading as="h2" size="lg" mb={4} color="blue.700">
              8. Changes to This Policy
            </Heading>
            <Text color={textColor} lineHeight="1.8">
              We may update this Privacy Policy periodically. We will notify you of significant changes by updating the date at the top of this page.
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
